import { useState, useCallback } from 'react';

export function useVideos(baseUrl, getToken) {
  const [videos, setVideos]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const apiUrl = useCallback(
    (path = '') => `${baseUrl}/videos${path}`,
    [baseUrl]
  );

  // Builds headers — always JSON, adds Bearer token when available
  const authHeaders = useCallback(() => {
    const token = getToken ? getToken() : null;
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [getToken]);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(apiUrl(), { headers: authHeaders() });
      if (!r.ok) {
        if (r.status === 401) throw new Error('Session expired. Please sign in again.');
        throw new Error('HTTP ' + r.status);
      }
      setVideos(await r.json());
    } catch (e) {
      setError('Could not load videos: ' + e.message);
      setVideos([]);
    }
    setLoading(false);
  }, [apiUrl, authHeaders]);

  const createVideo = useCallback(async (body) => {
    const r = await fetch(apiUrl(), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const t = await r.text();
      if (r.status === 401) throw new Error('Session expired. Please sign in again.');
      throw new Error(`HTTP ${r.status}: ${t}`);
    }
    await fetchVideos();
  }, [apiUrl, authHeaders, fetchVideos]);

  const updateVideo = useCallback(async (id, body) => {
    const r = await fetch(apiUrl('/' + id), {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const t = await r.text();
      if (r.status === 401) throw new Error('Session expired. Please sign in again.');
      throw new Error(`HTTP ${r.status}: ${t}`);
    }
    await fetchVideos();
  }, [apiUrl, authHeaders, fetchVideos]);

  const deleteVideo = useCallback(async (id) => {
    const r = await fetch(apiUrl('/' + id), {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!r.ok) {
      const t = await r.text();
      if (r.status === 401) throw new Error('Session expired. Please sign in again.');
      throw new Error(`HTTP ${r.status}: ${t}`);
    }
    await fetchVideos();
  }, [apiUrl, authHeaders, fetchVideos]);

  return { videos, loading, error, fetchVideos, createVideo, updateVideo, deleteVideo };
}
